import React, { useState, useEffect } from "react"
import {X, User, Calendar, MessageSquare, SquareCheck as CheckSquare, Plus, Send, Clock, FileText, Mail, Phone, Building2, ChevronDown, ChevronUp} from 'lucide-react'
import { lumi } from "../lib/lumi"

interface ContactRequest {
  _id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  company: string
  message?: string
  quizResponseId: string
  quizScore?: number
  quizTotalScore?: number
  quizResultLevel?: string
  quizResultMessage?: string
  quizResultRecommendation?: string
  quizResultColor?: string
  quizAnswers?: number[]
  status: 'pending' | 'contacted' | 'scheduled' | 'completed'
  assigned_to?: string
  assigned_to_name?: string
  comments?: Comment[]
  actions?: Action[]
  createdAt: string
  updatedAt: string
}

interface Comment {
  id: string
  author: string
  text: string
  createdAt: string
}

interface Action {
  id: string
  author: string
  type: 'call' | 'email' | 'meeting' | 'note' | 'other'
  description: string
  createdAt: string
}

interface TeamMember {
  _id: string
  name: string
  email: string
  role: string
  active: boolean
}

interface QuizResponse {
  _id: string
  answers: number[]
  totalScore: number
  percentage: number
  resultLevel: string
  createdAt: string
}

interface ContactRequestDetailProps {
  request: ContactRequest
  onClose: () => void
  onUpdate: () => void
}

export function ContactRequestDetail({ request, onClose, onUpdate }: ContactRequestDetailProps) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [newComment, setNewComment] = useState("")
  const [newAction, setNewAction] = useState({ type: 'note', description: '' })
  const [quizResponse, setQuizResponse] = useState<QuizResponse | null>(null)
  const [expandedQuiz, setExpandedQuiz] = useState(false)
  const [currentUser] = useState("Admin") // À remplacer par le système d'auth réel

  useEffect(() => {
    loadTeamMembers()
    if (request.quizResponseId) {
      loadQuizResponse()
    }
  }, [request.quizResponseId])

  const loadTeamMembers = async () => {
    try {
      const result = await lumi.entities.team_members.list({
        filter: { active: true },
        sort: { name: 1 }
      })
      setTeamMembers(result.list)
    } catch (error) {
      console.error('Erreur lors du chargement des membres:', error)
    }
  }

  const loadQuizResponse = async () => {
    try {
      const result = await lumi.entities.quiz_responses.list({
        filter: { _id: request.quizResponseId },
        limit: 1
      })
      if (result.list && result.list.length > 0) {
        setQuizResponse(result.list[0])
      }
    } catch (error) {
      console.error('Erreur lors du chargement du quiz:', error)
    }
  }

  const assignTo = async (memberId: string, memberName: string) => {
    try {
      await lumi.entities.contact_requests.update(request._id, {
        assigned_to: memberId,
        assigned_to_name: memberName,
        updatedAt: new Date().toISOString()
      })
      onUpdate()
    } catch (error) {
      console.error('Erreur lors de l\'assignation:', error)
    }
  }

  const addComment = async () => {
    if (!newComment.trim()) return
    
    try {
      const comment: Comment = {
        id: Date.now().toString(),
        author: currentUser,
        text: newComment,
        createdAt: new Date().toISOString()
      }
      
      const updatedComments = [...(request.comments || []), comment]
      await lumi.entities.contact_requests.update(request._id, {
        comments: updatedComments,
        updatedAt: new Date().toISOString()
      })
      
      setNewComment("")
      onUpdate()
    } catch (error) {
      console.error('Erreur lors de l\'ajout du commentaire:', error)
    }
  }

  const addAction = async () => {
    if (!newAction.description.trim()) return
    
    try {
      const action: Action = {
        id: Date.now().toString(),
        author: currentUser,
        type: newAction.type as any,
        description: newAction.description,
        createdAt: new Date().toISOString()
      }
      
      const updatedActions = [...(request.actions || []), action]
      await lumi.entities.contact_requests.update(request._id, {
        actions: updatedActions,
        updatedAt: new Date().toISOString()
      })
      
      setNewAction({ type: 'note', description: '' })
      onUpdate()
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'action:', error)
    }
  }

  const updateStatus = async (newStatus: string) => {
    try {
      await lumi.entities.contact_requests.update(request._id, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      })
      onUpdate()
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'call':
        return <Phone className="w-4 h-4" />
      case 'email':
        return <Mail className="w-4 h-4" />
      case 'meeting':
        return <Calendar className="w-4 h-4" />
      case 'note':
        return <FileText className="w-4 h-4" />
      default:
        return <CheckSquare className="w-4 h-4" />
    }
  }

  const getActionLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      call: 'Appel téléphonique',
      email: 'Email envoyé',
      meeting: 'Réunion',
      note: 'Note',
      other: 'Autre'
    }
    return labels[type] || type
  }

  const questions = [
    "À quelle fréquence prenez-vous du recul pour analyser la stratégie globale de votre entreprise ?",
    "Comment décririez-vous votre processus de prise de décision stratégique ?",
    "Avez-vous des tensions ou conflits récurrents dans votre entreprise (associés, équipe, management) ?",
    "Suivez-vous régulièrement vos indicateurs de performance (financiers, opérationnels, RH) ?",
    "Ressentez-vous une surcharge ou un sentiment d'être seul(e) face aux décisions ?",
    "Votre entreprise a-t-elle une vision stratégique claire à 1-3 ans ?",
    "Comment gérez-vous l'équilibre entre opérationnel et stratégie ?",
    "Avez-vous des outils ou processus structurés pour piloter votre entreprise ?",
    "Faites-vous appel à des regards extérieurs pour challenger vos décisions ?",
    "Comment décririez-vous votre capacité actuelle à faire croître votre entreprise ?",
    "Comment décririez-vous votre équilibre vie professionnelle / vie personnelle actuel ?",
    "Ressentez-vous des signes d'épuisement professionnel (fatigue chronique, stress, démotivation) ?",
    "Arrivez-vous à déconnecter complètement de votre entreprise (vacances, week-ends, soirées) ?",
    "Votre activité professionnelle impacte-t-elle négativement vos relations personnelles (famille, amis, couple) ?",
    "Prenez-vous soin de votre santé physique et mentale malgré vos responsabilités professionnelles ?"
  ]

  const getScoreColor = (score: number) => {
    if (score === 5) return 'text-red-600 bg-red-100'
    if (score === 4) return 'text-orange-600 bg-orange-100'
    if (score === 3) return 'text-amber-600 bg-amber-100'
    if (score === 2) return 'text-yellow-600 bg-yellow-100'
    if (score === 1) return 'text-lime-600 bg-lime-100'
    return 'text-green-600 bg-green-100'
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Dossier de {request.firstName} {request.lastName}</h2>
            <p className="text-teal-100 text-sm mt-1">Créé le {formatDate(request.createdAt)}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Colonne gauche: Informations et Quiz */}
            <div className="lg:col-span-2 space-y-6">
              {/* Informations de contact */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-teal-600" />
                  Informations de contact
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <a href={`mailto:${request.email}`} className="text-teal-600 hover:underline font-medium">
                        {request.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="text-xs text-gray-500">Téléphone</p>
                      <a href={`tel:${request.phone}`} className="text-teal-600 hover:underline font-medium">
                        {request.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="text-xs text-gray-500">Entreprise</p>
                      <p className="font-medium text-gray-900">{request.company}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="text-xs text-gray-500">Date de création</p>
                      <p className="font-medium text-gray-900">{formatDate(request.createdAt)}</p>
                    </div>
                  </div>
                </div>
                {request.message && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-1">Message:</p>
                    <p className="text-sm text-gray-600">{request.message}</p>
                  </div>
                )}
              </div>

              {/* Résultats du Quiz */}
              {request.quizScore !== undefined && (
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-teal-600" />
                      Résultats du Quiz
                    </h3>
                    <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                      request.quizResultColor === 'red' ? 'bg-red-100 text-red-700' :
                      request.quizResultColor === 'orange' ? 'bg-orange-100 text-orange-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      Score: {request.quizScore}/100
                    </span>
                  </div>
                  <div className="bg-white rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-lg font-semibold text-gray-900">{request.quizResultLevel}</p>
                      <p className="text-sm text-gray-700 mt-2">{request.quizResultMessage}</p>
                    </div>
                    {request.quizResultRecommendation && (
                      <div className="pt-3 border-t border-teal-200">
                        <p className="text-sm font-medium text-teal-900">
                          💡 Recommandation : {request.quizResultRecommendation}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Détail des réponses */}
                  {quizResponse && (
                    <div className="mt-4">
                      <button
                        onClick={() => setExpandedQuiz(!expandedQuiz)}
                        className="flex items-center gap-2 text-teal-700 hover:text-teal-800 font-medium transition-colors"
                      >
                        {expandedQuiz ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        <span>Voir le détail des {quizResponse.answers.length} réponses</span>
                      </button>
                      
                      {expandedQuiz && (
                        <div className="mt-3 space-y-2 bg-white/50 p-4 rounded-lg max-h-96 overflow-y-auto">
                          {quizResponse.answers.map((score, index) => (
                            <div key={index} className="bg-white p-3 rounded-lg border border-teal-100">
                              <div className="flex items-start justify-between gap-3">
                                <p className="text-sm text-gray-700 flex-1">
                                  <span className="font-semibold text-teal-700">Q{index + 1}.</span> {questions[index]}
                                </p>
                                <span className={`px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap ${getScoreColor(score)}`}>
                                  {score}/5
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Timeline: Actions et Commentaires */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-teal-600" />
                  Historique du dossier
                </h3>
                
                <div className="space-y-4">
                  {/* Mélanger et trier actions et commentaires par date */}
                  {[
                    ...(request.actions || []).map(a => ({ ...a, itemType: 'action' })),
                    ...(request.comments || []).map(c => ({ ...c, itemType: 'comment' }))
                  ]
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map((item: any) => (
                      <div key={item.id} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                        <div className={`mt-1 p-2 rounded-full ${
                          item.itemType === 'action' ? 'bg-teal-100 text-teal-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          {item.itemType === 'action' ? getActionIcon(item.type) : <MessageSquare className="w-4 h-4" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-1">
                            <div>
                              <p className="font-semibold text-gray-900">{item.author}</p>
                              {item.itemType === 'action' && (
                                <p className="text-xs text-gray-500">{getActionLabel(item.type)}</p>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">{formatDate(item.createdAt)}</p>
                          </div>
                          <p className="text-sm text-gray-700">{item.text || item.description}</p>
                        </div>
                      </div>
                    ))}
                  
                  {(!request.actions || request.actions.length === 0) && 
                   (!request.comments || request.comments.length === 0) && (
                    <p className="text-center text-gray-500 py-8">Aucun historique pour le moment</p>
                  )}
                </div>
              </div>
            </div>

            {/* Colonne droite: Gestion */}
            <div className="space-y-6">
              {/* Statut */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Statut du dossier</h3>
                <div className="space-y-2">
                  {['pending', 'contacted', 'scheduled', 'completed'].map(status => (
                    <button
                      key={status}
                      onClick={() => updateStatus(status)}
                      className={`w-full px-4 py-3 rounded-lg font-medium transition-all text-left ${
                        request.status === status
                          ? 'bg-teal-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {status === 'pending' && '⏳ En attente'}
                      {status === 'contacted' && '✉️ Contacté'}
                      {status === 'scheduled' && '📅 RDV planifié'}
                      {status === 'completed' && '✅ Terminé'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Assignation */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-teal-600" />
                  Assignation
                </h3>
                {request.assigned_to_name ? (
                  <div className="mb-4 p-3 bg-teal-50 rounded-lg">
                    <p className="text-sm text-gray-600">Assigné à:</p>
                    <p className="font-semibold text-teal-900">{request.assigned_to_name}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-4">Non assigné</p>
                )}
                <select
                  onChange={(e) => {
                    const member = teamMembers.find(m => m._id === e.target.value)
                    if (member) assignTo(member._id, member.name)
                  }}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                  value={request.assigned_to || ''}
                >
                  <option value="">Sélectionner un membre</option>
                  {teamMembers.map(member => (
                    <option key={member._id} value={member._id}>
                      {member.name} ({member.role})
                    </option>
                  ))}
                </select>
              </div>

              {/* Ajouter une action */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-teal-600" />
                  Ajouter une action
                </h3>
                <select
                  value={newAction.type}
                  onChange={(e) => setNewAction({ ...newAction, type: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none mb-3"
                >
                  <option value="note">📝 Note</option>
                  <option value="call">📞 Appel téléphonique</option>
                  <option value="email">✉️ Email envoyé</option>
                  <option value="meeting">🤝 Réunion</option>
                  <option value="other">➕ Autre</option>
                </select>
                <textarea
                  value={newAction.description}
                  onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
                  placeholder="Description de l'action..."
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none mb-3"
                />
                <button
                  onClick={addAction}
                  disabled={!newAction.description.trim()}
                  className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter l'action
                </button>
              </div>

              {/* Ajouter un commentaire */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-teal-600" />
                  Ajouter un commentaire
                </h3>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Votre commentaire..."
                  rows={4}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none mb-3"
                />
                <button
                  onClick={addComment}
                  disabled={!newComment.trim()}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Envoyer le commentaire
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
